import { apiClient, APIError } from "./client";
import endpoint from "@/constants/endpoint";
import type { SupportedLocale } from "@chinese-vendor/i18n";

type LocalizationBundleEntries = Record<string, string>;

/**
 * Type guard for a 404 thrown by `apiClient`.
 *
 * The client wraps non-OK responses in {@link APIError} with a flat `.status`
 * property. Several fetchers in this module want to convert a real "not found"
 * into `null` for React Query rather than letting it surface as an error.
 */
function is404(err: unknown): boolean {
  return err instanceof APIError && err.status === 404;
}

// ─── Public types ─────────────────────────────────────────────────────────────

export type ContentType = "categories" | "industries" | "other";
export type StatusFilter = "all" | "completed" | "pending";
export type SortField = "alpha_asc" | "alpha_desc" | "last_edited";

export interface LocalizationKey {
  key: string;
  /**
   * UUID used in PATCH API calls for industries and categories.
   * Undefined for "other" content type (the key is used directly).
   */
  id?: string;
  lastEditedAt: string | null;
  translations: {
    en: string | null;
    "zh-CN": string | null;
    ar: string | null;
  };
  isComplete: boolean;
  contentType: ContentType;
}

export interface LocalizationActivityEntry {
  id: string;
  actorName: string;
  actionType: "add" | "edit" | "delete";
  localeCode: string;
  localeLabel: string;
  createdAt: string;
}

export interface LocalizationKeyDetail {
  key: string;
  lastEditedAt: string | null;
  translations: {
    en: string | null;
    "zh-CN": string | null;
    ar: string | null;
  };
  contentType: ContentType;
  activityLog: LocalizationActivityEntry[];
}

export interface LocalizationListResponse {
  items: LocalizationKey[];
  total: number;
  completedCount: number;
  pendingCount: number;
  allCount: number;
  categoriesCount: number;
  industriesCount: number;
  otherCount: number;
  page: number;
  pageSize: number;
}

export interface LocalizationStatistics {
  totalEntries: number;
  totalCompleted: number;
  totalPending: number;
}

export interface ContentCountStats {
  total: number;
  completedCount: number;
  pendingCount: number;
}

export interface AddLocalizationKeyPayload {
  key: string;
  contentType: ContentType;
  en?: string;
  "zh-CN"?: string;
  ar?: string;
}

export interface UpdateTranslationPayload {
  en?: string;
  "zh-CN"?: string;
  ar?: string;
}

export interface ListOtherKeysParams {
  page: number;
  pageSize: number;
  search?: string;
  status: StatusFilter;
  sort: SortField;
}

export interface ListContentParams {
  page: number;
  pageSize: number;
  search?: string;
  status: StatusFilter;
  sort: SortField;
}

export interface OtherKeysPaginatedResponse {
  items: LocalizationKey[];
  total: number;
  completedCount: number;
  pendingCount: number;
  page: number;
  pageSize: number;
}

export interface ContentPaginatedResponse {
  items: LocalizationKey[];
  total: number;
  completedCount: number;
  pendingCount: number;
  page: number;
  pageSize: number;
}

export interface LocalizationActivityResponse {
  items: LocalizationActivityEntry[];
  total: number;
  page: number;
  pageSize: number;
}

export interface CreateIndustryPayload {
  translations: { en?: string; "zh-CN"?: string; ar?: string };
  sortOrder?: number;
  isActive?: boolean;
}

export interface CreateCategoryPayload {
  parentCategoryId?: string | null;
  translations: { en?: string; "zh-CN"?: string; ar?: string };
  sortOrder?: number;
  isActive?: boolean;
}

// ─── Backend response shapes (internal) ──────────────────────────────────────

interface _BackendEntryResponse {
  key: string;
  locale: string;
  value: string;
  updated_at: string;
}

interface _BackendKeyListItem {
  key: string;
  translations: Record<string, string>;
  is_complete: boolean;
  last_edited_at: string;
}

interface _BackendEntriesListResponse {
  items: _BackendKeyListItem[];
  total: number;
  completed_count: number;
  pending_count: number;
  page: number;
  page_size: number;
}

interface _BackendStatisticsResponse {
  total_entries: number;
  total_completed: number;
  total_pending: number;
}

interface _BackendIndustryItem {
  id: string;
  sort_order: number;
  is_active: boolean;
  translations: Record<string, string>;
  last_edited_at: string | null;
}

interface _BackendIndustryListResponse {
  items: _BackendIndustryItem[];
  total: number;
  completed_count: number;
  pending_count: number;
  page: number;
  page_size: number;
}

interface _BackendCategoryItem {
  id: string;
  parent_category_id: string | null;
  sort_order: number;
  is_active: boolean;
  translations: Record<string, string>;
  last_edited_at: string | null;
}

interface _BackendCategoryListResponse {
  items: _BackendCategoryItem[];
  total: number;
  completed_count: number;
  pending_count: number;
  page: number;
  page_size: number;
}

interface _BackendActivityItem {
  id: string;
  actor_name: string;
  action_type: "add" | "edit" | "delete";
  locale_code: string;
  locale_label: string;
  created_at: string;
}

interface _BackendActivityResponse {
  items: _BackendActivityItem[];
  total: number;
  page: number;
  page_size: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const _MAX_PAGE_SIZE = 100;

/** Fetches all pages of a paginated endpoint and returns a flat array of items. */
async function _fetchAllPages<T>(
  fetchPage: (page: number) => Promise<{ items: T[]; total: number }>
): Promise<T[]> {
  const first = await fetchPage(1);
  const all: T[] = [...first.items];
  const totalPages = Math.ceil(first.total / _MAX_PAGE_SIZE);
  const remaining = Array.from({ length: totalPages - 1 }, (_, i) => fetchPage(i + 2));
  const rest = await Promise.all(remaining);
  for (const r of rest) all.push(...r.items);
  return all;
}

// ─── Transformers ─────────────────────────────────────────────────────────────

function _backendKeyListItemToKey(item: _BackendKeyListItem): LocalizationKey {
  return {
    key: item.key,
    lastEditedAt: item.last_edited_at ?? null,
    translations: {
      en: item.translations["en"] || null,
      "zh-CN": item.translations["zh-CN"] || null,
      ar: item.translations["ar"] || null,
    },
    isComplete: item.is_complete,
    contentType: "other",
  };
}

function _industryToKey(item: _BackendIndustryItem): LocalizationKey {
  return {
    key: item.translations["en"] || item.id,
    id: item.id,
    lastEditedAt: item.last_edited_at ?? null,
    translations: {
      en: item.translations["en"] || null,
      "zh-CN": item.translations["zh-CN"] || null,
      ar: item.translations["ar"] || null,
    },
    isComplete: ["en", "zh-CN", "ar"].every((l) => !!item.translations[l]),
    contentType: "industries",
  };
}

function _categoryToKey(item: _BackendCategoryItem): LocalizationKey {
  return {
    key: item.translations["en"] || item.id,
    id: item.id,
    lastEditedAt: item.last_edited_at ?? null,
    translations: {
      en: item.translations["en"] || null,
      "zh-CN": item.translations["zh-CN"] || null,
      ar: item.translations["ar"] || null,
    },
    isComplete: ["en", "zh-CN", "ar"].every((l) => !!item.translations[l]),
    contentType: "categories",
  };
}

// ─── API ──────────────────────────────────────────────────────────────────────

export const localizationApi = {
  // ── Bundle (used by localizationSync) ──────────────────────────────────────

  async getBundle(locale: SupportedLocale): Promise<LocalizationBundleEntries> {
    const res = await apiClient.get<LocalizationBundleEntries>(
      `${endpoint.localization.bundle}?locale=${locale}`
    );
    return res.data;
  },

  // ── List endpoints ─────────────────────────────────────────────────────────

  /**
   * Server-side paginated list of "other" i18n keys.
   * Uses GET /localization/entries with filter/sort/pagination params.
   */
  async listOtherKeysPaginated(params: ListOtherKeysParams): Promise<OtherKeysPaginatedResponse> {
    const query = new URLSearchParams({
      page: String(params.page),
      page_size: String(params.pageSize),
      sort: params.sort,
      status: params.status,
    });
    if (params.search?.trim()) query.set("search", params.search.trim());

    const res = await apiClient.get<_BackendEntriesListResponse>(
      `${endpoint.localization.entries}?${query}`
    );
    return {
      items: res.data.items.map(_backendKeyListItemToKey),
      total: res.data.total,
      completedCount: res.data.completed_count,
      pendingCount: res.data.pending_count,
      page: res.data.page,
      pageSize: res.data.page_size,
    };
  },

  /** Global unfiltered statistics for "other" i18n keys (for stat cards). */
  async getStatistics(): Promise<LocalizationStatistics> {
    const res = await apiClient.get<_BackendStatisticsResponse>(endpoint.localization.statistics);
    return {
      totalEntries: res.data.total_entries,
      totalCompleted: res.data.total_completed,
      totalPending: res.data.total_pending,
    };
  },

  /**
   * Fetch all industries as LocalizationKey items (all pages, max 100 per page).
   * Used by the detail screen to find an industry by id or key.
   */
  async listIndustriesAll(): Promise<LocalizationKey[]> {
    return _fetchAllPages<LocalizationKey>(async (page) => {
      const res = await apiClient.get<_BackendIndustryListResponse>(
        `${endpoint.localization.industries}?page=${page}&page_size=${_MAX_PAGE_SIZE}`
      );
      return { items: res.data.items.map(_industryToKey), total: res.data.total };
    });
  },

  /** Server-side paginated list of industries. */
  async listIndustriesPaginated(params: ListContentParams): Promise<ContentPaginatedResponse> {
    const query = new URLSearchParams({
      page: String(params.page),
      page_size: String(params.pageSize),
      sort: params.sort,
      status: params.status,
    });
    if (params.search?.trim()) query.set("search", params.search.trim());
    const res = await apiClient.get<_BackendIndustryListResponse>(
      `${endpoint.localization.industries}?${query}`
    );
    return {
      items: res.data.items.map(_industryToKey),
      total: res.data.total,
      completedCount: res.data.completed_count,
      pendingCount: res.data.pending_count,
      page: res.data.page,
      pageSize: res.data.page_size,
    };
  },

  /**
   * Fetch all product categories as LocalizationKey items (all pages, max 100 per page).
   * Used by the detail screen to find a category by id or key.
   */
  async listCategoriesAll(): Promise<LocalizationKey[]> {
    return _fetchAllPages<LocalizationKey>(async (page) => {
      const res = await apiClient.get<_BackendCategoryListResponse>(
        `${endpoint.localization.productCategories}?page=${page}&page_size=${_MAX_PAGE_SIZE}`
      );
      return { items: res.data.items.map(_categoryToKey), total: res.data.total };
    });
  },

  /** Server-side paginated list of product categories. */
  async listCategoriesPaginated(params: ListContentParams): Promise<ContentPaginatedResponse> {
    const query = new URLSearchParams({
      page: String(params.page),
      page_size: String(params.pageSize),
      sort: params.sort,
      status: params.status,
    });
    if (params.search?.trim()) query.set("search", params.search.trim());
    const res = await apiClient.get<_BackendCategoryListResponse>(
      `${endpoint.localization.productCategories}?${query}`
    );
    return {
      items: res.data.items.map(_categoryToKey),
      total: res.data.total,
      completedCount: res.data.completed_count,
      pendingCount: res.data.pending_count,
      page: res.data.page,
      pageSize: res.data.page_size,
    };
  },

  // ── Detail ─────────────────────────────────────────────────────────────────

  /**
   * Fetch all three locale translations for a single "other" i18n key.
   * Three parallel GET /entries/{key}?locale= requests.
   * 404 → null (key not yet translated for that locale).
   * Other errors (auth, 5xx, network) are rethrown so callers can handle them.
   */
  async getOtherKeyTranslations(key: string): Promise<LocalizationKeyDetail> {
    const fetchLocale = async (locale: string) => {
      try {
        const res = await apiClient.get<_BackendEntryResponse>(
          `${endpoint.localization.entry(key)}?locale=${locale}`
        );
        return res.data;
      } catch (err) {
        if (is404(err)) return null;
        throw err;
      }
    };

    const [enData, zhData, arData] = await Promise.all([
      fetchLocale("en"),
      fetchLocale("zh-CN"),
      fetchLocale("ar"),
    ]);

    const dates = [enData, zhData, arData]
      .filter((d): d is _BackendEntryResponse => d !== null)
      .map((d) => d.updated_at);
    const lastEditedAt = dates.length ? dates.reduce((a, b) => (a > b ? a : b)) : null;

    return {
      key,
      lastEditedAt,
      translations: {
        en: enData?.value ?? null,
        "zh-CN": zhData?.value ?? null,
        ar: arData?.value ?? null,
      },
      contentType: "other",
      activityLog: [],
    };
  },

  // ── Mutations ──────────────────────────────────────────────────────────────

  /**
   * Update one or more locale values for a single "other" i18n key.
   * Only non-empty values are sent (backend requires min_length=1).
   */
  async updateOtherTranslations(
    key: string,
    values: { en?: string; zhCn?: string; ar?: string }
  ): Promise<void> {
    const calls: Promise<unknown>[] = [];
    if (values.en?.trim())
      calls.push(
        apiClient.patch(endpoint.localization.entry(key), { locale: "en", value: values.en.trim() })
      );
    if (values.zhCn?.trim())
      calls.push(
        apiClient.patch(endpoint.localization.entry(key), {
          locale: "zh-CN",
          value: values.zhCn.trim(),
        })
      );
    if (values.ar?.trim())
      calls.push(
        apiClient.patch(endpoint.localization.entry(key), { locale: "ar", value: values.ar.trim() })
      );
    await Promise.all(calls);
  },

  /** Update one or more locale names for a single industry. */
  async updateIndustryTranslations(
    id: string,
    values: { en?: string; zhCn?: string; ar?: string }
  ): Promise<void> {
    const calls: Promise<unknown>[] = [];
    if (values.en?.trim())
      calls.push(
        apiClient.patch(endpoint.localization.industryEntry(id), {
          locale: "en",
          name: values.en.trim(),
        })
      );
    if (values.zhCn?.trim())
      calls.push(
        apiClient.patch(endpoint.localization.industryEntry(id), {
          locale: "zh-CN",
          name: values.zhCn.trim(),
        })
      );
    if (values.ar?.trim())
      calls.push(
        apiClient.patch(endpoint.localization.industryEntry(id), {
          locale: "ar",
          name: values.ar.trim(),
        })
      );
    await Promise.all(calls);
  },

  /** Update one or more locale names for a single product category. */
  async updateCategoryTranslations(
    id: string,
    values: { en?: string; zhCn?: string; ar?: string }
  ): Promise<void> {
    const calls: Promise<unknown>[] = [];
    if (values.en?.trim())
      calls.push(
        apiClient.patch(endpoint.localization.productCategoryEntry(id), {
          locale: "en",
          name: values.en.trim(),
        })
      );
    if (values.zhCn?.trim())
      calls.push(
        apiClient.patch(endpoint.localization.productCategoryEntry(id), {
          locale: "zh-CN",
          name: values.zhCn.trim(),
        })
      );
    if (values.ar?.trim())
      calls.push(
        apiClient.patch(endpoint.localization.productCategoryEntry(id), {
          locale: "ar",
          name: values.ar.trim(),
        })
      );
    await Promise.all(calls);
  },

  /**
   * Create a new "other" i18n key with initial translations via bulk import.
   * Only non-empty locale values are submitted.
   */
  async addOtherContent(payload: AddLocalizationKeyPayload): Promise<void> {
    const entries: { key: string; locale: string; value: string }[] = [];
    if (payload.en?.trim())
      entries.push({ key: payload.key, locale: "en", value: payload.en.trim() });
    if (payload["zh-CN"]?.trim())
      entries.push({ key: payload.key, locale: "zh-CN", value: payload["zh-CN"].trim() });
    if (payload.ar?.trim())
      entries.push({ key: payload.key, locale: "ar", value: payload.ar.trim() });
    if (entries.length === 0) throw new Error("At least one translation is required");
    await apiClient.post(endpoint.localization.import, { entries });
  },

  /** Create a new industry with initial translations. */
  async createIndustry(payload: CreateIndustryPayload): Promise<LocalizationKey> {
    const res = await apiClient.post<_BackendIndustryItem>(endpoint.localization.industries, {
      translations: {
        en: payload.translations.en ?? null,
        "zh-CN": payload.translations["zh-CN"] ?? null,
        ar: payload.translations.ar ?? null,
      },
      sort_order: payload.sortOrder ?? 0,
      is_active: payload.isActive ?? true,
    });
    return _industryToKey(res.data);
  },

  /** Create a new product category with initial translations. */
  async createCategory(payload: CreateCategoryPayload): Promise<LocalizationKey> {
    const res = await apiClient.post<_BackendCategoryItem>(
      endpoint.localization.productCategories,
      {
        parent_category_id: payload.parentCategoryId ?? null,
        translations: {
          en: payload.translations.en ?? null,
          "zh-CN": payload.translations["zh-CN"] ?? null,
          ar: payload.translations.ar ?? null,
        },
        sort_order: payload.sortOrder ?? 0,
        is_active: payload.isActive ?? true,
      }
    );
    return _categoryToKey(res.data);
  },

  /** Fetch paginated activity log for a translation key. */
  async getActivityLog(
    key: string,
    page = 1,
    pageSize = 20
  ): Promise<LocalizationActivityResponse> {
    const query = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    const res = await apiClient.get<_BackendActivityResponse>(
      `${endpoint.localization.entryActivity(key)}?${query}`
    );
    return {
      items: res.data.items.map(
        (item): LocalizationActivityEntry => ({
          id: item.id,
          actorName: item.actor_name,
          actionType: item.action_type,
          localeCode: item.locale_code,
          localeLabel: item.locale_label,
          createdAt: item.created_at,
        })
      ),
      total: res.data.total,
      page: res.data.page,
      pageSize: res.data.page_size,
    };
  },

  // ── Vendor / sync helpers ──────────────────────────────────────────────────

  /** Localization staff: update a single string entry (used by localizationSync). */
  async updateEntry(key: string, locale: SupportedLocale, value: string): Promise<void> {
    await apiClient.patch<null>(endpoint.localization.entry(key), { locale, value });
  },

  /** Get all industries with localized names (raw, for vendor dropdowns). */
  async getIndustries(locale: SupportedLocale): Promise<{ id: string; name: string }[]> {
    return _fetchAllPages<{ id: string; name: string }>(async (page) => {
      const res = await apiClient.get<_BackendIndustryListResponse>(
        `${endpoint.localization.industries}?page=${page}&page_size=${_MAX_PAGE_SIZE}`
      );
      return {
        items: res.data.items.map((i) => ({
          id: i.id,
          name: i.translations[locale] || i.translations["en"] || "",
        })),
        total: res.data.total,
      };
    });
  },

  /** Get all product categories with localized names (raw, for vendor dropdowns). */
  async getProductCategories(
    locale: SupportedLocale,
    industryIds?: string[]
  ): Promise<{ id: string; name: string; parentCategoryId: string }[]> {
    return _fetchAllPages<{ id: string; name: string; parentCategoryId: string }>(async (page) => {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(_MAX_PAGE_SIZE),
      });
      if (industryIds?.length) params.set("industry_ids", industryIds.join(","));
      const res = await apiClient.get<_BackendCategoryListResponse>(
        `${endpoint.localization.productCategories}?${params}`
      );
      return {
        items: res.data.items.map((c) => ({
          id: c.id,
          name: c.translations[locale] || c.translations["en"] || "",
          parentCategoryId: c.parent_category_id ?? "",
        })),
        total: res.data.total,
      };
    });
  },

  /**
   * Fetch a single industry by id and return its localized name.
   * Returns null when the industry does not exist (404).
   */
  async getIndustryById(
    id: string,
    locale: SupportedLocale
  ): Promise<{ id: string; name: string } | null> {
    try {
      const res = await apiClient.get<_BackendIndustryItem>(
        endpoint.localization.industryEntry(id)
      );
      return {
        id: res.data.id,
        name: res.data.translations[locale] || res.data.translations["en"] || "",
      };
    } catch (err) {
      if (is404(err)) return null;
      throw err;
    }
  },

  /**
   * Fetch a single product category by id and return its localized name.
   * Returns null when the category does not exist (404).
   */
  async getCategoryById(
    id: string,
    locale: SupportedLocale
  ): Promise<{ id: string; name: string } | null> {
    try {
      const res = await apiClient.get<_BackendCategoryItem>(
        endpoint.localization.productCategoryEntry(id)
      );
      return {
        id: res.data.id,
        name: res.data.translations[locale] || res.data.translations["en"] || "",
      };
    } catch (err) {
      if (is404(err)) return null;
      throw err;
    }
  },

  /** Lightweight unfiltered counts for industries (for stat cards). */
  async getIndustryStats(): Promise<ContentCountStats> {
    const res = await apiClient.get<_BackendIndustryListResponse>(
      `${endpoint.localization.industries}?page=1&page_size=1&status=all`
    );
    return {
      total: res.data.total,
      completedCount: res.data.completed_count,
      pendingCount: res.data.pending_count,
    };
  },

  /** Lightweight unfiltered counts for product categories (for stat cards). */
  async getCategoryStats(): Promise<ContentCountStats> {
    const res = await apiClient.get<_BackendCategoryListResponse>(
      `${endpoint.localization.productCategories}?page=1&page_size=1&status=all`
    );
    return {
      total: res.data.total,
      completedCount: res.data.completed_count,
      pendingCount: res.data.pending_count,
    };
  },
};
